import React, { useState, useEffect, useCallback } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc, Product } from 'src/trpc';
import { DataTable } from 'components/table/DataTable';
import { SortingState } from '@tanstack/react-table';
import { NewProductSheet } from 'pages/Product/NewProductSheet';
import { EditProductSheet } from 'pages/Product/EditProductSheet';
import { productColumns } from 'pages/Product/ProductColumns';
import { Search } from 'components/Search';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'components/Debounce';
import { useOrganization } from 'src/context/OrganizationContext';
import { Loader } from 'components/Loader';

interface ProductWithActions extends Product {
  onEdit: (product: Product) => void;
}

const ProductsDashboard: React.FC = () => {
  const [products, setProducts] = useState<ProductWithActions[]>([]);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [params, setParams] = useSearchParams({
    offset: '0',
    limit: '20',
    search: '',
  });

  // Use organization context for org-dependent queries
  const { organizationId, isResolved, isOrganizationSwitching, setIsOrganizationSwitching } =
    useOrganization();

  const [search, setSearch] = useState(params.get('search') ?? '');
  const debouncedSearch = useDebounce(search, 500);

  const offset = parseInt(params.get('offset')!);
  const limit = parseInt(params.get('limit')!);
  const currentPage = offset === 0 ? 1 : offset / limit + 1;

  const { data, isLoading, refetch, isFetching } = trpc.admin.product.getAllProducts.useQuery(
    {
      includeArchived: true,
      offset,
      limit,
      search: params.get('search') || '',
    },
    {
      placeholderData: keepPreviousData,
      enabled: isResolved && !!organizationId,
    },
  );

  // Reset organization switching state when fetch completes
  useEffect(() => {
    if (isOrganizationSwitching && !isFetching) {
      setIsOrganizationSwitching(false);
    }
  }, [isOrganizationSwitching, isFetching, setIsOrganizationSwitching]);

  useEffect(() => {
    if (data) {
      const productsWithEditAction: ProductWithActions[] = data.products.map((product) => ({
        ...product,
        onEdit: (product: Product) => setEditingProduct(product),
      }));
      setProducts(productsWithEditAction);
    }
  }, [data]);

  const updateSearchParams = useCallback(
    (searchValue: string) => {
      const newParams = new URLSearchParams(params);
      newParams.set('search', searchValue);
      newParams.set('offset', '0');
      setParams(newParams);
    },
    [params, setParams],
  );

  useEffect(() => {
    const currentSearch = params.get('search') ?? '';
    if (debouncedSearch !== currentSearch) {
      updateSearchParams(debouncedSearch);
    }
  }, [debouncedSearch, params, updateSearchParams]);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handlePageChange = useCallback(
    (change: number) => {
      const newParams = new URLSearchParams(params);
      newParams.set('offset', (offset + change * limit).toString());
      setParams(newParams);
    },
    [offset, limit, params, setParams],
  );

  if (!isResolved || isOrganizationSwitching) {
    return <Loader title="Getting products" />;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold mb-4">Products</h2>
      <div className="flex justify-between items-center space-x-4">
        <div className="flex-grow">
          <Search
            value={search}
            onChange={handleSearch}
            placeholder="Search products by name, description, or type"
            isLoading={isLoading || isOrganizationSwitching}
          />
        </div>
        <NewProductSheet onSuccess={() => refetch()} />
      </div>
      <div className="flex flex-col h-full">
        <DataTable
          columns={productColumns}
          data={products}
          sorting={sorting}
          onSortingChange={setSorting}
          onPageChange={handlePageChange}
          pageCount={Math.ceil((data?.total || 0) / limit)}
          currentPage={currentPage}
          total={data?.total || 0}
          resourceName="products"
          offset={offset}
          limit={limit}
        />
      </div>
      <EditProductSheet
        product={editingProduct}
        onClose={() => setEditingProduct(null)}
        onSuccess={() => {
          refetch();
          setEditingProduct(null);
        }}
      />
    </div>
  );
};

export default ProductsDashboard;
